import React from "react";
import Toolbar from "@material-ui/core/Toolbar";
import { Divider, Box } from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import DropdownMenu from "./common/DropdownMenu";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchReportAnalyticsConfig,
  setPerformanceReportDateRange,
} from "store/performance-report/PerformanceReportActions";
import { setPerformanceReportStoreId } from "store/performance-report/PerformanceReportActions";
import { resetAllProducAnalyticsView } from "store/product/ProductActions";
import { resetKpiConfig } from "store/kpi-config/KpiConfigActions";
import { periodTypes } from "utils/Constants";
import { useHasEntitlement } from "hooks/useEntitlement";
import { selectAllStoreInfo } from "store/store-list/StoreListSelectors";
import { LoadCatalogList } from "store/catalog/CatalogActions";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      display: "flex",
      justifyContent: "center",
      gridTemplateColumns: "repeat(3,1fr)",
      backgroundColor: "#F2F3F4",
      minHeight: "fit-content",
    },
    topnav: {
      display: "flex",
      justifyContent: "space-between",
      margin: "15px",
    },
    formControl: {
      margin: "8px",
      "& .MuiSelect-select": {
        minWidth: "160px",
      },
    },
  }),
);

interface PrimaryToolbarSequenceProps {
  navigationTabs: React.ComponentType;
  showTopNavigation?: boolean;
  disableDateRangeSelector?: boolean;
}

const PrimaryToolbarSequence = ({
  navigationTabs: NavigationTabs,
  showTopNavigation = false,
  disableDateRangeSelector = false,
}: PrimaryToolbarSequenceProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const hasCatalogEntitlement = useHasEntitlement("catalog");
  const storeInfo = useSelector(selectAllStoreInfo);

  const handlePerformanceStoreChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const changeEvent = event as React.ChangeEvent<{ value: string }>;
    if (hasCatalogEntitlement) {
      const selectedStoreInfo = storeInfo[changeEvent.target.value];
      const defaultCatalogId =
        selectedStoreInfo?.storeDefaults?.catalogId || undefined;
      if (!defaultCatalogId) {
        dispatch(LoadCatalogList(changeEvent.target.value));
      }
    }
    // Close all open analytics when store changes
    dispatch(resetAllProducAnalyticsView());
    dispatch(resetKpiConfig());
    dispatch(
      setPerformanceReportStoreId({ storeId: changeEvent.target.value }),
    );
    dispatch(
      fetchReportAnalyticsConfig({
        storeId: changeEvent.target.value,
      }),
    );
  };

  const handlePerformanceDateRangeChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const changeEvent = event as React.ChangeEvent<{ value: string }>;
    // Close all open analytics when date range changes
    dispatch(resetAllProducAnalyticsView());
    dispatch(
      setPerformanceReportDateRange({ dateRange: changeEvent.target.value }),
    );
  };

  const performanceStoreMenuId = "PerformanceReportStoreSelection";
  const performanceDateRangeMenuId = "DateRangeSelection";

  return (
    <Box>
      {showTopNavigation && (
        <Box className={classes.topnav}>
          <DropdownMenu
            menuId={performanceStoreMenuId}
            classes={classes}
            handleStoreChange={handlePerformanceStoreChange}
          />
          <DropdownMenu
            menuId={performanceDateRangeMenuId}
            classes={classes}
            handleDateRangeChange={handlePerformanceDateRangeChange}
            dateRangeOptions={periodTypes}
            disableDateRangeSelector={disableDateRangeSelector}
          />
        </Box>
      )}
      <Toolbar className={classes.toolbar}>
        <NavigationTabs />
      </Toolbar>
      <Divider />
    </Box>
  );
};

export default PrimaryToolbarSequence;
